import React, { useEffect, useMemo, useRef, useState } from "react";
import Pagination from "@mui/material/Pagination";
import { Drawer, Button, Space, notification } from "antd";
import Dialog from "@mui/material/Dialog";
import { Typography } from "antd";
import * as XLSX from "xlsx";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { Link } from "react-router-dom";

import ShowData from "../../../../components/ShowData";
import SkeltonList from "../../../../components/SkeltonEffect/list";
import SmartRtpLayout from "./SmartRtpLayout";
import { getPage } from "../../../../controllers/common";
import { list as generalData } from "../../../../controllers/V1/cms/generalDataController";
import { AID_STATUS_TABS } from "../config";

function buildExportPeriod(fromDate, toDate) {
  const period = {};
  if (fromDate) period.period_start = fromDate;
  if (toDate) period.period_end = toDate;
  return period;
}

function buildDateTimeLocalFromGeneralTime(timeStr, fallbackTime) {
  const date = moment().format("YYYY-MM-DD");
  if (timeStr) {
    const parsed = moment(`2000-01-01 ${timeStr}`, [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "HH:mm:ss",
      "HH:mm",
    ], true);
    if (parsed.isValid()) {
      return `${date}T${parsed.format("HH:mm")}`;
    }
  }
  return `${date}T${fallbackTime}`;
}

function mapGameWiseExportRows(rows = [], gameLookup = {}) {
  return rows.map((row) => {
    const gameId = row?._id ? String(row._id) : "";
    const gameMeta = gameLookup[gameId] || {};
    return {
      Game: gameMeta.title || row?.game_code || gameId || "-",
      "Game Code": row?.game_code || gameMeta.game_code || "-",
      Orders: row?.orders || 0,
      Stake: row?.stake || 0,
      Wins: row?.wins || 0,
      "Scheduled Big Prizes": row?.scheduled_big_prize_count || 0,
      "Scheduled BP Amount": row?.scheduled_big_prize_amount || 0,
    };
  });
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return "-";
    }
  }
  return String(value);
}

function exportCell(column, row) {
  if (column.key === "actions" || column.showInExcel === false || column.excludeFromExport) {
    return undefined;
  }
  if (typeof column.exportValue === "function") return column.exportValue(row);
  const raw = row?.[column.key];
  if (raw !== null && raw !== undefined && typeof raw !== "object") return raw;
  if (typeof column.render === "function") {
    const rendered = column.render(row, 0);
    if (typeof rendered === "string" || typeof rendered === "number" || typeof rendered === "boolean") {
      return rendered;
    }
  }
  if (raw == null) return "";
  if (typeof raw === "object") return JSON.stringify(raw);
  return raw;
}

function buildFilterCondition(filter, searchFields = [], searchNumericFields = []) {
  const condition = {};
  if (filter.search && searchFields.length) {
    if (filter.searchField) {
      if (searchNumericFields.includes(filter.searchField)) {
        const numericValue = Number(filter.search);
        condition[filter.searchField] = Number.isNaN(numericValue)
          ? { $in: [] }
          : numericValue;
      } else {
        condition[filter.searchField] = { $regex: filter.search, $options: "i" };
      }
    } else {
      condition.$or = searchFields.map((field) => ({
        [field]: { $regex: filter.search, $options: "i" },
      }));
    }
  }
  if (filter.from_date) {
    condition.createdAt = {
      $gte: filter.from_date,
      $lte: filter.to_date || new Date(),
    };
  }
  return condition;
}

export default function SmartRtpTablePage({
  title,
  activeKey,
  fetchData,
  columns,
  buildOptions,
  emptyMessage = "No data found !",
  extraTop = null,
  actions = null,
  statusTabs = AID_STATUS_TABS,
  statusField = "status",
  searchFields = ["title"],
  exportFileName = "Smart RTP Export",
  showFilter = true,
  showExport = true,
  exportConfig = null,
  refreshToken = 0,
  searchOptions = [],
  searchNumericFields = [],
}) {
  const targetRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [statusIndex, setStatusIndex] = useState(0);
  const [isFilterShow, setFilterShow] = useState(false);
  const [isExportShow, setExportShow] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [filter, setFilter] = useState({ search: "", searchField: "", from_date: "", to_date: "" });
  const [gameOptions, setGameOptions] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [exportDates, setExportDates] = useState({ from_date: "", to_date: "" });

  const statusValue = statusTabs[statusIndex]?.value ?? "";

  const buildListOptions = ({ forExport = false } = {}) => {
    const statusCondition = statusValue ? { [statusField]: statusValue } : {};
    const filterCondition = buildFilterCondition(filter, searchFields, searchNumericFields);
    const base = buildOptions
      ? buildOptions({ currentPage, limit, skip, statusValue, filter })
      : {
          type: "",
          condition: {},
          select: {},
          sort: { _id: -1 },
          skip,
          limit,
        };
    return {
      ...base,
      condition: {
        ...(base.condition || {}),
        ...statusCondition,
        ...filterCondition,
      },
      ...(forExport ? { isExport: true, skip: 0, limit: 0 } : { skip, limit }),
    };
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const res = await fetchData(buildListOptions());
      if (res.status === true) {
        const nextRows = Array.isArray(res.result) ? res.result : [];
        setRows(nextRows);
        setTotalPages(getPage(res.count || 1, limit));
      } else {
        setRows([]);
        notification.open({
          message: "Oops!",
          description: res.message || emptyMessage,
        });
      }
    } catch (error) {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    targetRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentPage, limit, skip, refreshFlag, statusIndex, filter, refreshToken]);

  useEffect(() => {
    document.title = `Crown King || ${title}`;
  }, [title]);

  useEffect(() => {
    if (!isExportShow || !exportConfig?.showGameSelect || !exportConfig?.loadGames) {
      return undefined;
    }
    let cancelled = false;
    const loadGames = async () => {
      setGamesLoading(true);
      try {
        const res = await exportConfig.loadGames();
        if (cancelled) return;
        if (res?.status === true && Array.isArray(res.result)) {
          setGameOptions(res.result);
        } else {
          setGameOptions([]);
        }
      } catch (error) {
        if (!cancelled) setGameOptions([]);
      } finally {
        if (!cancelled) setGamesLoading(false);
      }
    };
    loadGames();
    return () => {
      cancelled = true;
    };
  }, [isExportShow, exportConfig]);

  useEffect(() => {
    if (!isExportShow || !exportConfig?.useGeneralDataExportTimes) {
      return undefined;
    }
    let cancelled = false;
    const loadExportDates = async () => {
      try {
        // const res = await generalData({});
        const res = {
          status: true,
          result: {
            export_start_time: "00:00",
            export_end_time: "23:59",
          },
        };
        if (cancelled) return;
        if (res?.status === true) {
          const { export_start_time: exportStartTime, export_end_time: exportEndTime } =
            res.result || {};
          setExportDates({
            from_date: buildDateTimeLocalFromGeneralTime(exportStartTime, "00:00"),
            to_date: buildDateTimeLocalFromGeneralTime(
              exportEndTime,
              moment().format("HH:mm")
            ),
          });
        } else {
          setExportDates({
            from_date: `${moment().format("YYYY-MM-DD")}T00:00`,
            to_date: moment().format("YYYY-MM-DDTHH:mm"),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setExportDates({
            from_date: `${moment().format("YYYY-MM-DD")}T00:00`,
            to_date: moment().format("YYYY-MM-DDTHH:mm"),
          });
        }
      }
    };
    loadExportDates();
    return () => {
      cancelled = true;
    };
  }, [isExportShow, exportConfig?.useGeneralDataExportTimes]);

  const handleStatusChange = (event, value) => {
    setStatusIndex(value);
    setCurrentPage(1);
    setSkip(0);
  };

  const handleFilterApply = (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    setFilter({
      search: form.get("search") || "",
      searchField: form.get("searchField") || "",
      from_date: form.get("from_date") || "",
      to_date: form.get("to_date") || "",
    });
    setCurrentPage(1);
    setSkip(0);
    setFilterShow(false);
  };

  const handleFilterReset = () => {
    setFilter({ search: "", searchField: "", from_date: "", to_date: "" });
    setCurrentPage(1);
    setSkip(0);
  };

  const handleExport = async (e) => {
    e.preventDefault();
    setExportError(null);
    setIsExporting(true);
    try {
      const form = new FormData(e.target);
      const useGeneralExportTimes = exportConfig?.useGeneralDataExportTimes;
      const exportFilter = {
        search: exportConfig?.hideSearch
          ? ""
          : form.get("search") || filter.search || "",
        searchField: exportConfig?.hideSearch
          ? ""
          : form.get("searchField") || filter.searchField || "",
        from_date: useGeneralExportTimes
          ? exportDates.from_date
          : form.get("from_date") || filter.from_date || "",
        to_date: useGeneralExportTimes
          ? exportDates.to_date
          : form.get("to_date") || filter.to_date || "",
      };
      const exportType = form.get("export_type") || "orders";
      const gameId = form.get("game_id") || "";
      const gameLookup = gameOptions.reduce((acc, game) => {
        if (game?._id) acc[String(game._id)] = game;
        return acc;
      }, {});

      if (exportType === "game-wise" && exportConfig?.fetchGameWiseReport) {
        const res = await exportConfig.fetchGameWiseReport({
          ...(gameId ? { game_id: gameId } : {}),
          ...buildExportPeriod(exportFilter.from_date, exportFilter.to_date),
        });
        const rows = Array.isArray(res?.result) ? res.result : [];
        if (res?.status === true && rows.length > 0) {
          const data = mapGameWiseExportRows(rows, gameLookup);
          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.json_to_sheet(data);
          XLSX.utils.book_append_sheet(workbook, worksheet, "Game-wise Report");
          XLSX.writeFile(
            workbook,
            `Smart RTP Game-wise Report ${moment().format("YYYY-MM-DD")}.xlsx`
          );
          setExportShow(false);
        } else {
          setExportError("No game-wise report data found for the selected criteria.");
        }
        return;
      }

      const statusCondition = statusValue ? { [statusField]: statusValue } : {};
      const base = buildListOptions({ forExport: true });
      const res = await fetchData({
        ...base,
        isExport: true,
        skip: 0,
        limit: 0,
        condition: {
          ...(base.condition || {}),
          ...statusCondition,
          ...buildFilterCondition(exportFilter, searchFields, searchNumericFields),
          ...(gameId ? { game_id: gameId } : {}),
        },
      });
      if (res.status === true && res.result?.length > 0) {
        const data = res.result.map((row) => {
          const item = {};
          columns.forEach((column) => {
            const value = exportCell(column, row);
            if (value !== undefined) item[column.label] = value;
          });
          return item;
        });
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, `${exportFileName} ${moment().format("YYYY-MM-DD")}.xlsx`);
        setExportShow(false);
      } else {
        setExportError("No records found matching the criteria.");
      }
    } catch (error) {
      setExportError("An error occurred while exporting the data.");
    } finally {
      setIsExporting(false);
    }
  };

  const renderedColumns = useMemo(
    () => (columns || []).filter((column) => column.showInOrderList !== false),
    [columns]
  );

  return (
    <div ref={targetRef}>
      <SmartRtpLayout
        title={title}
        activeKey={activeKey}
        headerVariant="list"
        statusTabs={statusTabs}
        statusIndex={statusIndex}
        onStatusChange={handleStatusChange}
        showFilter={showFilter}
        showExport={showExport}
        onFilter={() => setFilterShow(true)}
        onExport={() => setExportShow(true)}
        onRefresh={() => setRefreshFlag((prev) => !prev)}
        extraHeader={<ShowData setLimit={(value) => { setLimit(Number(value)); setCurrentPage(1); setSkip(0); }} limit={limit} />}
        actions={actions}
      >
        {extraTop}
        <div id="content-wrapper" className="d-flex flex-column">
          <div className="card shadow mb-4">
            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table table-bordered" width="100%">
                  <thead>
                    <tr>
                      <th>Sl. No.</th>
                      {renderedColumns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0
                      ? rows.map((row, index) => (
                          <tr key={row._id || `${index}-${row.id || "row"}`}>
                            <td>{skip + index + 1}</td>
                            {renderedColumns.map((column) => (
                              <td key={column.key}>
                                {column.render
                                  ? column.render(row, index)
                                  : displayValue(row?.[column.key])}
                              </td>
                            ))}
                          </tr>
                        ))
                      : null}
                    {!rows.length && isLoading ? (
                      <SkeltonList row={5} col={renderedColumns.length + 1} />
                    ) : null}
                    {!rows.length && !isLoading ? (
                      <tr>
                        <td colSpan={renderedColumns.length + 1} className="img-center">
                          {emptyMessage}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <div className="table_pagination_sales_order">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => {
                    setCurrentPage(page);
                    setSkip((page - 1) * limit);
                  }}
                  color="primary"
                />
              </div>
            </div>
          </div>
        </div>
      </SmartRtpLayout>

      <Drawer
        title="Filter"
        placement="right"
        width={420}
        onClose={() => setFilterShow(false)}
        open={isFilterShow}
        extra={
          <Space>
            <Button onClick={() => setFilterShow(false)}>Cancel</Button>
            <Button type="primary" onClick={handleFilterReset} style={{ backgroundColor: "var(--brand-red)", color: "white" }}>
              Reset
            </Button>
          </Space>
        }
      >
        <form onSubmit={handleFilterApply}>
          <div className="row">
            <div className="form-group col-12 mb-3">
              <label className="all_section_for_label_main_class">Search:</label>
              {searchOptions.length ? (
                <select
                  name="searchField"
                  className="form-control store_input_field mb-2"
                  defaultValue={filter.searchField || searchOptions[0].value}
                >
                  {searchOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : null}
              <input type="text" name="search" className="form-control store_input_field" defaultValue={filter.search} placeholder="Search..." />
            </div>
            <div className="form-group col-6 mb-3">
              <label className="all_section_for_label_main_class">From Date:</label>
              <input type="datetime-local" name="from_date" className="form-control store_input_field" defaultValue={filter.from_date} />
            </div>
            <div className="form-group col-6 mb-3">
              <label className="all_section_for_label_main_class">To Date:</label>
              <input type="datetime-local" name="to_date" className="form-control store_input_field" defaultValue={filter.to_date} />
            </div>
            <div className="form-group col-12 mt-2">
              <button type="submit" className="form-control btn btn-black" style={{ backgroundColor: "var(--brand-red-dark)", color: "white" }}>
                Apply Filter
              </button>
            </div>
          </div>
        </form>
      </Drawer>

      <Dialog
        open={isExportShow}
        onClose={() => setExportShow(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: "myDialogPopUp" }}
      >
        <div className="myDrawer">
          <div className="inventory_popup_title">
            <Typography variant="h5" component="div" gutterBottom className="inventory_popup_title_heading">
              Export {title}
            </Typography>
            <Link to={false} style={{ position: "absolute", right: 8, top: 8, color: "#dd5151" }} onClick={() => setExportShow(false)}>
              <FontAwesomeIcon icon={faTimesCircle} />
            </Link>
          </div>
          <Typography variant="body1" component="div" className="my_delete_popup_paragraph">
            <div className="container-fluid">
              <form onSubmit={handleExport}>
                <div className="form-group row">
                  {exportConfig?.enableGameWiseReport ? (
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
                      <label className="all_lable_for_vendor_dashbaord">Export Type:</label>
                      <select name="export_type" defaultValue="orders" className="form-control categories_select_tg_enter_product">
                        <option value="orders">Orders List</option>
                        <option value="game-wise">Game-wise Report</option>
                      </select>
                    </div>
                  ) : null}
                  {exportConfig?.showGameSelect ? (
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
                      <label className="all_lable_for_vendor_dashbaord">Game:</label>
                      <select
                        name="game_id"
                        defaultValue=""
                        className="form-control categories_select_tg_enter_product"
                        disabled={gamesLoading}
                      >
                        <option value="">All Games</option>
                        {gameOptions.map((game) => (
                          <option key={game._id} value={game._id}>
                            {game.title || game.game_code || game._id}
                            {game.game_code ? ` (${game.game_code})` : ""}
                          </option>
                        ))}
                      </select>
                      {gamesLoading ? (
                        <small className="text-muted">Loading games...</small>
                      ) : null}
                    </div>
                  ) : null}
                  {!exportConfig?.hideSearch ? (
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                      <label className="all_lable_for_vendor_dashbaord">Search:</label>
                      {searchOptions.length ? (
                        <select name="searchField" defaultValue={filter.searchField || searchOptions[0].value} className="form-control categories_select_tg_enter_product mb-2">
                          {searchOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <input type="text" name="search" defaultValue={filter.search} className="form-control categories_select_tg_enter_product" />
                    </div>
                  ) : null}
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label className="all_lable_for_vendor_dashbaord">From:</label>
                    <input
                      type="datetime-local"
                      name="from_date"
                      value={exportConfig?.useGeneralDataExportTimes ? exportDates.from_date : undefined}
                      defaultValue={exportConfig?.useGeneralDataExportTimes ? undefined : filter.from_date}
                      onChange={
                        exportConfig?.useGeneralDataExportTimes
                          ? (event) =>
                              setExportDates((prev) => ({
                                ...prev,
                                from_date: event.target.value,
                              }))
                          : undefined
                      }
                      className="form-control categories_select_tg_enter_product"
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label className="all_lable_for_vendor_dashbaord">To:</label>
                    <input
                      type="datetime-local"
                      name="to_date"
                      value={exportConfig?.useGeneralDataExportTimes ? exportDates.to_date : undefined}
                      defaultValue={exportConfig?.useGeneralDataExportTimes ? undefined : filter.to_date}
                      onChange={
                        exportConfig?.useGeneralDataExportTimes
                          ? (event) =>
                              setExportDates((prev) => ({
                                ...prev,
                                to_date: event.target.value,
                              }))
                          : undefined
                      }
                      className="form-control categories_select_tg_enter_product"
                    />
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div className="inventory_open_popup_action_btn">
                      <button type="button" className="btn btn-light categories_cancel_button" onClick={() => setExportShow(false)}>Cancel</button>
                      <button
                        type="submit"
                        className="btn btn-danger"
                        disabled={
                          isExporting ||
                          (exportConfig?.useGeneralDataExportTimes &&
                            (!exportDates.from_date || !exportDates.to_date))
                        }
                      >
                        Download Excel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </Typography>
          {exportError ? <div style={{ color: "red", padding: "0 16px 16px" }}>{exportError}</div> : null}
        </div>
      </Dialog>
    </div>
  );
}
